import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';
import { PAYMENT_STATUS } from '../services/paymentService';

const AgentDashboard = ({ user, userData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referredUsers, setReferredUsers] = useState([]);
  const [stats, setStats] = useState({
    totalReferred: 0,
    jobSeekers: 0,
    employers: 0,
    totalRevenue: 0,
    totalCommission: 0
  });
  const [referralLink, setReferralLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!user || !userData) return;
    
    const fetchReferredUsers = async () => {
      try {
        // Generate referral link using production domain
        const link = `https://udaanjobs.com/register?ref=${user.uid}`;
        setReferralLink(link);

        // Fetch users referred by this agent
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('referredBy', '==', user.uid));
        const querySnapshot = await getDocs(q);
        
        // Function to calculate tiered commission
        const calculateTieredCommission = (amount, tiers) => {
          if (!tiers || tiers.length === 0) {
            // Fallback to flat rate if no tiers
            const flatRate = userData?.commissionRate ?? 10;
            return (amount * flatRate) / 100;
          }

          let totalCommission = 0;
          let remainingAmount = amount;

          // Sort tiers by minAmount to ensure proper calculation
          const sortedTiers = [...tiers].sort((a, b) => a.minAmount - b.minAmount);

          for (const tier of sortedTiers) {
            if (remainingAmount <= 0) break;
            
            // Calculate the amount that falls within this tier
            const tierMin = tier.minAmount;
            const tierMax = tier.maxAmount;
            
            if (amount > tierMin) {
              const applicableAmount = Math.min(amount, tierMax) - tierMin;
              if (applicableAmount > 0) {
                const tierCommission = (applicableAmount * tier.percentage) / 100;
                totalCommission += tierCommission;
              }
            }
          }

          return totalCommission;
        };

        const referralsWithPayments = await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const referralData = { id: docSnap.id, ...docSnap.data() };

            const paymentsQuery = query(
              collection(db, 'payments'),
              where('userId', '==', docSnap.id),
              where('status', '==', PAYMENT_STATUS.SUCCESS)
            );

            const paymentsSnap = await getDocs(paymentsQuery);
            const totalRevenue = paymentsSnap.docs.reduce((sum, paymentDoc) => {
              const payment = paymentDoc.data();
              return sum + (payment.totalAmount || payment.amount || 0);
            }, 0);

            const commission = calculateTieredCommission(totalRevenue, userData?.commissionTiers);

            return {
              ...referralData,
              totalRevenue,
              commission,
              paymentsCount: paymentsSnap.size
            };
          })
        );

        let seekerCount = 0;
        let employerCount = 0;
        let totalRevenue = 0;
        let totalCommission = 0;

        referralsWithPayments.forEach((referral) => {
          totalRevenue += referral.totalRevenue;
          totalCommission += referral.commission;

          if (referral.userType === 'jobseeker') {
            seekerCount++;
          } else if (referral.userType === 'employer') {
            employerCount++;
          }
        });

        setReferredUsers(referralsWithPayments);
        setStats({
          totalReferred: referralsWithPayments.length,
          jobSeekers: seekerCount,
          employers: employerCount,
          totalRevenue,
          totalCommission
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching referred users:', error);
        setLoading(false);
      }
    };

    fetchReferredUsers();
  }, [user, userData]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Dashboard</h1>
        <p className="text-gray-600">Welcome, {userData?.firstName} {userData?.lastName}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Referred</p>
              <p className="text-3xl font-bold">{stats.totalReferred}</p>
              <p className="text-blue-100 text-sm mt-1">All users</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Job Seekers</p>
              <p className="text-3xl font-bold">{stats.jobSeekers}</p>
              <p className="text-green-100 text-sm mt-1">Looking for jobs</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Employers</p>
              <p className="text-3xl font-bold">{stats.employers}</p>
              <p className="text-purple-100 text-sm mt-1">Hiring talent</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Total Revenue</p>
              <p className="text-3xl font-bold">₹{stats.totalRevenue.toLocaleString()}</p>
              <p className="text-emerald-100 text-sm mt-1">From successful referrals</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1m-5.197 0C9.92 8.402 10.89 8 12 8m0-4v4m0 8v4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">
                Total Commission {userData?.commissionTiers && userData.commissionTiers.length > 0 ? '(Tiered)' : `(${userData?.commissionRate ?? 10}%)`}
              </p>
              <p className="text-3xl font-bold">₹{stats.totalCommission.toLocaleString()}</p>
              <p className="text-amber-100 text-sm mt-1">Based on referral earnings</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Commission Tiers Section */}
      {userData?.commissionTiers && userData.commissionTiers.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Your Commission Structure</h2>
              <p className="text-sm text-gray-600">Commission rates based on payment amount ranges</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userData.commissionTiers.map((tier, index) => (
              <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border-2 border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-200 px-2 py-1 rounded-full">
                    Tier {index + 1}
                  </span>
                  <span className="text-2xl font-bold text-emerald-600">{tier.percentage}%</span>
                </div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Min:</span>
                    <span className="font-semibold">₹{tier.minAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Max:</span>
                    <span className="font-semibold">₹{tier.maxAmount.toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-xs text-gray-600">
                    Earn <span className="font-bold text-emerald-700">{tier.percentage}%</span> commission on amounts between ₹{tier.minAmount.toLocaleString()} - ₹{tier.maxAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Example Calculation */}
          <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-indigo-700">
                <p className="font-semibold mb-2">How it works:</p>
                <p>Your commission is calculated based on the payment amount falling within each tier range. For example, if a referral makes a payment of ₹12,000:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  {userData.commissionTiers.map((tier, idx) => {
                    const exampleAmount = 12000;
                    if (exampleAmount > tier.minAmount) {
                      const applicableAmount = Math.min(exampleAmount, tier.maxAmount) - tier.minAmount;
                      const commission = (applicableAmount * tier.percentage) / 100;
                      if (applicableAmount > 0) {
                        return (
                          <li key={idx}>
                            ₹{tier.minAmount.toLocaleString()}-₹{tier.maxAmount.toLocaleString()} at {tier.percentage}% = ₹{commission.toLocaleString()}
                          </li>
                        );
                      }
                    }
                    return null;
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Link Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Your Referral Link</h2>
        </div>
        <p className="text-gray-600 mb-4">Share this link with job seekers and employers to track their registrations</p>
        <div className="flex gap-3">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 font-mono text-sm"
          />
          <button
            onClick={copyToClipboard}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
          >
            {copySuccess ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Link
              </>
            )}
          </button>
        </div>
      </div>

      {/* Referred Users List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Referred Users</h2>
        </div>

        {referredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Revenue</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Commission</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Joined Date</th>
                </tr>
              </thead>
              <tbody>
                {referredUsers.map((refUser, index) => (
                  <tr key={refUser.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors`}>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {refUser.firstName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{refUser.firstName} {refUser.lastName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-700">{refUser.email}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-700">{refUser.phone || 'N/A'}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        refUser.userType === 'jobseeker' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {refUser.userType === 'jobseeker' ? 'Job Seeker' : 'Company'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-700">
                        ₹{refUser.totalRevenue.toLocaleString()} ({refUser.paymentsCount} payments)
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-700">₹{refUser.commission.toLocaleString()}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-700">
                        {refUser.createdAt?.toDate?.()?.toLocaleDateString() || 
                         new Date(refUser.createdAt).toLocaleDateString() || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No referred users yet</h3>
            <p className="text-gray-500">Share your referral link to start tracking users!</p>
          </div>
        )}
      </div>
    </div>
    
    {/* Footer */}
    <Footer />
  </>
  );
};

export default AgentDashboard;
